// main tools
import { useRef } from 'react'
import Link from 'next/link'

// components
import { IconComponent } from '@centria-ui-atoms/icon-component'
import { OverlayPanel } from '@centria-ui-atoms/overlay-panel'
import { Typography } from '@centria-ui-atoms/typography'
import { Tooltip } from '@centria-ui-atoms/tooltip'

// Styles
import classes from './styles.module.scss'

// types
import type { OverlayPanel as PrOverlayPanel } from 'primereact/overlaypanel'
import type { IconName } from '@centria-ui-atoms/icon-component'
import type { ReactNode, MouseEvent, FC } from 'react'

export type MenuDropdownItemDataType = {
  href?: string
  label: string
  icon?: IconName
  hidden?: boolean
  disabled?: string
  action?: () => void
}

type MenuDropdownProps = {
  disabled?: boolean
  children: ReactNode
  colClassName?: string
  options: MenuDropdownItemDataType[]
}

export const MenuDropdown: FC<MenuDropdownProps> = ({
  options,
  disabled,
  children,
  colClassName
}) => {
  const overlay = useRef<PrOverlayPanel>(null)

  const templateItem = ({
    icon,
    label,
    disabled
  }: MenuDropdownItemDataType) => (
    <>
      {icon && <IconComponent icon={icon} color='inherit' className='me-2' />}
      <Typography
        Tag='span'
        fontSize='small'
        fontcolor={disabled ? 'light' : 'dark'}>
        {label}
      </Typography>
    </>
  )

  const handleToggle = (e: MouseEvent<HTMLElement>) => {
    if (!disabled) overlay.current?.toggle(e)
  }

  const handleAction = (action?: () => void) => {
    if (action) action()
    overlay.current?.hide()
  }

  return (
    <>
      <div className={colClassName} onClick={handleToggle} role='button'>
        {children}
      </div>
      <OverlayPanel closeIcon panelRef={overlay} className={classes.panel}>
        {options
          ?.filter((option) => !option.hidden)
          .map(({ href, action, ...item }, key) =>
            item.disabled ? (
              <span key={key} className={classes.panel_item}>
                <Tooltip tooltip={item.disabled}>{templateItem(item)}</Tooltip>
              </span>
            ) : href ? (
              <Link key={key} className={classes.panel_item} href={href}>
                {templateItem(item)}
              </Link>
            ) : (
              <span
                key={key}
                role='button'
                className={classes.panel_item}
                onClick={() => handleAction(action)}>
                {templateItem(item)}
              </span>
            )
          )}
      </OverlayPanel>
    </>
  )
}
